import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Button from '../components/ui/button';
import Input from '../components/ui/input';
import Label from '../components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import Badge from '../components/ui/badge';
import { Mail, Users, Send, Trash2, Clock } from 'lucide-react';
import { notifications } from '../lib/notifications';
import axios from '../utils/axios';

const TeamInvitations = () => {
  const { user } = useAuth();
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    role: 'Team Member'
  });
  const [errors, setErrors] = useState({});

  const roles = [
    { value: 'customer_user', label: 'Team Member' },
    { value: 'customer_admin', label: 'Admin' },
  ];

  useEffect(() => {
    fetchInvitations();
  }, []);

  const fetchInvitations = async () => {
    try {
      const response = await axios.get('/team/invitations');

      // Handle different response formats
      let invitationsData = [];
      if (response.data?.invitations?.data) {
        // Paginated response
        invitationsData = response.data.invitations.data;
      } else if (response.data?.invitations) {
        // Direct array response
        invitationsData = response.data.invitations;
      } else if (response.data?.data) {
        // Alternative structure
        invitationsData = response.data.data;
      } else if (Array.isArray(response.data)) {
        // Direct array
        invitationsData = response.data;
      }

      setInvitations(Array.isArray(invitationsData) ? invitationsData : []);
    } catch (error) {
      console.error('Failed to fetch invitations:', error);
      // Start with empty array - clean slate for new users
      setInvitations([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    setSending(true);

    // Validate form
    const newErrors = {};
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setSending(false);
      return;
    }

    try {
      await axios.post('/team/invitations', formData);
      notifications.success('Invitation sent', `Team invitation sent to ${formData.email}`);
      setFormData({ email: '', role: 'Team Member' });
      fetchInvitations();
    } catch (error) {
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      } else {
        notifications.error('Failed to send invitation', 'Please try again later');
      }
    } finally {
      setSending(false);
    }
  };

  const handleDeleteInvitation = async (id) => {
    if (window.confirm('Are you sure you want to cancel this invitation?')) {
      try {
        await axios.delete(`/team/invitations/${id}`);
        notifications.success('Invitation cancelled', 'The invitation has been cancelled');
        fetchInvitations();
      } catch (error) {
        notifications.error('Failed to cancel invitation', 'Please try again later');
      }
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { variant: 'secondary', color: 'bg-yellow-100 text-yellow-800', label: 'Pending' },
      accepted: { variant: 'default', color: 'bg-green-100 text-green-800', label: 'Accepted' },
      expired: { variant: 'outline', color: 'bg-red-100 text-red-800', label: 'Expired' },
    };

    const config = statusConfig[status] || statusConfig.pending;
    return (
      <Badge className={config.color}>
        {config.label}
      </Badge>
    );
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h1>
          <p className="text-gray-600">Please log in to access team invitations.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32 flex-1">
          {/* Header */}
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-foreground mb-6 bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text">
              Invite & Collaborate
              <br />
              <span className="bg-gradient-to-r from-primary to-green-600 bg-clip-text text-transparent">
                Every Team
              </span>
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
              Build powerful validation teams by inviting colleagues and managing access to your
              LLM testing workspace
            </p>
          </div>

          <div className="max-w-4xl mx-auto space-y-8">
            {/* Invite Team Member Card */}
            <Card className="border border-border shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Invite Team Member
                </CardTitle>
                <CardDescription>
                  Add new team members to collaborate on LLM validation projects
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="email">Email Address</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                        <Input
                          id="email"
                          type="email"
                          placeholder="team@llmchecks.com"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          className={`pl-10 ${errors.email ? 'border-red-500' : ''}`}
                          required
                        />
                      </div>
                      {errors.email && (
                        <p className="mt-1 text-sm text-red-600">{errors.email[0] || errors.email}</p>
                      )}
                    </div>

                    <div>
                      <Label htmlFor="role">Role</Label>
                      <Select
                        value={formData.role}
                        onValueChange={(value) => setFormData({ ...formData, role: value })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select role" />
                        </SelectTrigger>
                        <SelectContent>
                          {roles.map((role) => (
                            <SelectItem key={role.value} value={role.value}>
                              {role.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-blue-600 hover:bg-blue-700"
                    disabled={sending}
                  >
                    {sending ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Sending Invitation...
                      </>
                    ) : (
                      <>
                        <Send className="mr-2 w-4 h-4" />
                        Send Invitation
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Current Invitations */}
            <Card className="border border-border shadow-sm">
              <CardHeader>
                <CardTitle>Current Invitations</CardTitle>
                <CardDescription>
                  Manage pending and active team invitations
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
                    <p className="text-muted-foreground">Loading invitations...</p>
                  </div>
                ) : invitations.length === 0 ? (
                  <div className="text-center py-8">
                    <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-medium mb-2">No invitations yet</h3>
                    <p className="text-muted-foreground">
                      Start building your team by sending your first invitation above.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {invitations.map((invitation) => (
                      <div
                        key={invitation.id}
                        className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                            <Mail className="w-5 h-5 text-blue-600" />
                          </div>
                          <div>
                            <div className="font-medium">{invitation.email}</div>
                            <div className="text-sm text-muted-foreground flex items-center gap-2">
                              <span>Invited by {typeof invitation.invited_by === 'object' ? invitation.invited_by?.name || 'Unknown' : invitation.invited_by}</span>
                              <span>•</span>
                              <span>{invitation.role}</span>
                              <span>•</span>
                              <div className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                <span>Expires {new Date(invitation.expires_at).toLocaleDateString()}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          {getStatusBadge(invitation.status)}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDeleteInvitation(invitation.id)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeamInvitations;
